import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import CustomDatePicker from "../../../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../../../components/CustomSelect";
import CustomTextArea from "../../../../../../../components/CustomTextArea";
import Input from "../../../../../../../components/Input";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { usePatientCareplanCreate } from "../../../../../../../queries/care-plan/use-patient-careplan-create";
import { usePatientCareplanUpdate } from "../../../../../../../queries/care-plan/use-patient-careplan-update";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getUnNecessaryTransformData,
  getXtenantId,
} from "../../../../../../../utils/utils";
import { STATUS_LIST } from "../allergy/allergy-constant";
import { CurFormState } from "./ActiveCareplan";
import {
  CAREPLAN_INITIAL_VALUE,
  CAREPLAN_VALIDATION_SCHEMA,
  Careplan,
} from "./careplan-constant";
import dayjs from "dayjs";
import { getStartEndDateOfSameDay } from "../../../../../../../utils/date-time-utility";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import Flex from "../../../../../../../components/Flex";
import MaskMultiSelectScroll from "../../../../../../../components/MaskMultiSelectScroll";
import useGetCataLogCode from "../../../../../../../queries/catalog/use-get-catalog-code";
import { useDebounce } from "../../../../../../../hooks/use-debounce";

interface UpsertCarePlanDrawerProps {
  disclosureHost: DisclosureHost;
  curFormState: CurFormState;
  setCurFormState?: Dispatch<SetStateAction<CurFormState>>;
}

const UpsertCarePlanDrawer = ({
  disclosureHost,
  curFormState,
  setCurFormState,
}: UpsertCarePlanDrawerProps) => {
  // const patientData: any = useOutletContext();
  const queryClient = useQueryClient();
  const { mutateAsync: createCareplan } = usePatientCareplanCreate();
  const { mutateAsync: updateCareplan } = usePatientCareplanUpdate();
  const { patientId } = useParams();

  const disabled = curFormState === "view";
  const showSnackbar = useSnackbar();
  const formik = useFormik<Careplan>({
    initialValues: disclosureHost?.data ?? CAREPLAN_INITIAL_VALUE,
    validationSchema: CAREPLAN_VALIDATION_SCHEMA,
    onSubmit: submitCareplanHandler,
    enableReinitialize: true,
  });

  async function submitCareplanHandler(values?: any) {
    const patient = {
      [patientId ?? ""]: patientId ?? "",
    };

    const payload = {
      carePlan: {
        ...getUnNecessaryTransformData(["icdCodes"], values),
        patient,
      },
      xTENANTID: getXtenantId(),
    };
    if (disclosureHost?.data) showToastAndResetForm(updateCareplan(payload));
    else showToastAndResetForm(createCareplan(payload));
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    apiCall
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));

        queryClient.invalidateQueries({
          queryKey: ["patient-careplan-list"],
        });

        setTimeout(closeAndReset, 1500);
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  function closeAndReset() {
    formik.resetForm();
    disclosureHost?.close();
    setCurFormState?.("add");
  }

  const [searchIcdKey, setSearchIcdKey] = useDebounce<string>("");
  const [icdPagination, setIcdPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: icdCodes, isFetching: isIcdFetching } = useGetCataLogCode({
    pagination: icdPagination,
    searchKey: searchIcdKey,
    type: "ICD",
    active: true,
    archive: false,
    enabled: disclosureHost.opened,
  });

  const transFormedIcdCodes = useMemo(() => {
    const fetched =
      icdCodes?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: `${it?.code} - ${it?.description || ""}`,
      })) ?? [];
    const selected = (formik.values?.icdCodes ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => (o as any)[idKey] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [icdCodes, formik.values?.icdCodes]);

  return (
    <StyledFormModal
      open={disclosureHost?.opened}
      onClose={closeAndReset}
      dialogTitle={disclosureHost?.data ? "Edit Care Plan" : "Add Care Plan"}
      dialogAction={
        <Flex justifyContent={"flex-end"} gap={1}>
          <StyledButtonV2
            label="Save"
            variant="contained"
            onClick={() => {
              formik.submitForm();
            }}
          />
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={closeAndReset}
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "40vw",
          height: "70vh",
        },
      }}
    >
      <Box pt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Input
              label="Title"
              required
              textFieldProps={{
                placeholder: "Individualized Care Plan",
                name: "title",
                disabled,
                value: formik.values.title,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              }}
              error={formik.touched?.title && formik.errors?.title}
            />
          </Grid>
          <Grid item xs={12}>
            {/* note condition value is removed  */}
            {/* https://medarch.atlassian.net/browse/ECRH-3299 */}
            {/* <Input
              label="Condition"
              textFieldProps={{
                placeholder: "Enter Condition",
                name: "condition",
                disabled,
                value: formik.values.condition,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              }}
            /> */}
            <MaskMultiSelectScroll
              // disabled={false}
              label="ICD Codes"
              placeholder="Search & Select ICD Codes"
              value={formik.values?.icdCodes ?? undefined}
              onSearch={setSearchIcdKey}
              options={transFormedIcdCodes}
              onChange={(v) => formik.setFieldValue("icdCodes", v)}
              pagination={icdPagination}
              setPagination={setIcdPagination}
              totalElements={icdCodes?.data?.page?.totalElements}
              loading={isIcdFetching}
            />
          </Grid>
          <Grid item xs={4}>
            <CustomDatePicker
              placeholder="Start Date"
              required
              // maxDate={dayjs(new Date())}
              disabled={disabled}
              slotProps={{
                inputAdornment: {
                  position: "end",
                },
              }}
              maxDate={
                formik.values?.endDate
                  ? dayjs(formik.values.endDate)
                  : undefined
              }
              value={
                formik.values?.startDate
                  ? new Date(formik.values.startDate)
                  : undefined
              }
              label="Start Date"
              onChange={(val) =>
                formik.setFieldValue("startDate", val?.toISOString())
              }
              error={formik.touched?.startDate && formik.errors?.startDate}
            />
          </Grid>
          <Grid item xs={4}>
            <CustomDatePicker
              disabled={disabled}
              placeholder="End Date"
              minDate={
                formik.values?.startDate
                  ? dayjs(formik.values.startDate)
                  : undefined
              }
              slotProps={{
                inputAdornment: {
                  position: "end",
                },
              }}
              value={
                formik.values?.endDate
                  ? new Date(formik.values.endDate)
                  : undefined
              }
              label="End Date"
              onChange={(val) => {
                const { endTime } = getStartEndDateOfSameDay(val);

                formik.setFieldValue("endDate", endTime);
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <CustomSelect
              value={formik.values.active as any}
              label="Status"
              placeholder="Select"
              name="active"
              readOnly={disabled}
              handleBlur={formik.handleBlur}
              disableSortOptions
              options={STATUS_LIST as any}
              onChange={(v) =>
                formik.setFieldValue("active", (v ?? undefined) as any)
              }
            />
          </Grid>

          <Grid item xs={12}>
            <CustomTextArea
              label="Goals"
              id="outlined-multiline-static"
              rows={2}
              variant="outlined"
              name="goals"
              placeholder="Enter Goals"
              value={formik.values.goals}
              onChange={(e) => {
                formik.setFieldValue("goals", e.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextArea
              label="Preventive Schedule"
              id="outlined-multiline-static"
              rows={2}
              variant="outlined"
              name="preventive_schedule"
              placeholder="Enter Preventive Schedule"
              value={formik.values.preventive_schedule}
              onChange={(e) => {
                formik.setFieldValue("preventive_schedule", e.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextArea
              label="Intervention"
              id="outlined-multiline-static"
              rows={2}
              variant="outlined"
              name="intervention"
              placeholder="Enter Intervention"
              value={formik.values.intervention}
              onChange={(e) => {
                formik.setFieldValue("intervention", e.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextArea
              label="Description"
              id="outlined-multiline-static"
              rows={5}
              variant="outlined"
              disabled={disabled}
              name="deception"
              placeholder="Enter Description"
              onBlur={formik.handleBlur}
              value={formik.values?.deception}
              onChange={formik.handleChange}
            />
          </Grid>
        </Grid>
      </Box>
    </StyledFormModal>
  );
};

export default UpsertCarePlanDrawer;

// const TitleMapper: Record<CurFormState, string> = {
//   add: "Add New Care Plan",
//   edit: "Edit Care Plan",
//   view: "View Care Plan",
// };
